import { requestEnableAudioModeration, requestEnableDesktopModeration, requestEnableVideoModeration } from '../../av-moderation/actions';
import { APP_WILL_MOUNT } from '../../base/app/actionTypes';
import { CONFERENCE_FAILED, CONFERENCE_JOINED } from '../../base/conference/actionTypes';
import { updateConfig } from '../../base/config/actions';
import { JitsiConferenceErrors } from '../../base/lib-jitsi-meet';
import { DOMINANT_SPEAKER_CHANGED, PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../../base/participants/actionTypes';
import { pinParticipant } from '../../base/participants/actions';
import { isLocalParticipantModerator } from '../../base/participants/functions';
import MiddlewareRegistry from '../../base/redux/MiddlewareRegistry';
import { updateSettings } from '../../base/settings/actions';
import { TRACK_ADDED, TRACK_REMOVED, TRACK_UPDATED } from '../../base/tracks/actionTypes';
import { setFilmstripVisible } from '../../filmstrip/actions.any';
import { selectParticipantInLargeVideo } from '../../large-video/actions.any';
import { toggleLobbyMode } from '../../lobby/actions.any';
import { setPrejoinPageVisibility } from '../../prejoin/actions.web';
import { setPreferredVideoQuality } from '../../video-quality/actions';
import { MAX_VIDEO_QUALITY } from '../../video-quality/constants';
MiddlewareRegistry.register(({ dispatch, getState }) => next => action => {
    if (action.type === APP_WILL_MOUNT) {
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('config.liveCoachingMode');
        console.log('[LiveCoaching] APP_WILL_MOUNT — mode détecté:', mode);
        if (mode === 'viewer' || mode === 'coach') {
            console.log(`[LiveCoaching] ${mode} → désactivation prejoin + saisie nom`);
            dispatch(updateConfig({
                prejoinConfig: { enabled: false },
                requireDisplayName: false
            }));
            dispatch(setPrejoinPageVisibility(false));
            // Désactivation du son de notification de message entrant pour le chat TheBellyLab
            dispatch(updateSettings({ soundsIncomingMessage: false }));
            const name = params.get('name');
            if (name) {
                console.log(`[LiveCoaching] Nom défini depuis l'URL: "${name}"`);
                dispatch(updateSettings({ displayName: name }));
            }
            // Client-specific logo passed via ?logo=<url> (encoded). Stored in sessionStorage
            // so the existing Watermarks component picks it up. A CSS brightness(0)+invert(1)
            // filter is also injected to force the logo to render in pure white regardless
            // of its source colour.
            const clientLogo = params.get('logo');
            if (clientLogo) {
                console.log(`[LiveCoaching] Logo client défini: "${clientLogo}"`);
                try {
                    sessionStorage.setItem('VISIO_COMPANY_LOGO', clientLogo);
                }
                catch (_) { /* ignore */ }
                const logoStyle = document.createElement('style');
                logoStyle.id = 'live-coaching-client-logo-style';
                logoStyle.textContent = `
                    .leftwatermark, .watermark.leftwatermark {
                        filter: brightness(0) invert(1) !important;
                    }
                `;
                document.head.appendChild(logoStyle);
            }
        }
        if (mode === 'coach') {
            console.log('[LiveCoaching] Coach → injection CSS + polling enforcer du focus caméra locale');
            // Disable stage filmstrip — required for pinParticipant to actually control large video.
            // (When stage filmstrip is active, getPinnedParticipant reads from activeParticipants
            // instead of state.pinnedParticipant, so our pin would be ignored.)
            // disableChat hides the built-in Jitsi chat button — the TBL-Chat overlay is used instead.
            dispatch(updateConfig({
                filmstrip: { disableStageFilmstrip: true },
                disableChat: true
            }));
            // Polling enforcer: every 1s, make sure the coach is pinned and on the large video.
            // This bypasses all middleware ordering/timing pitfalls.
            const enforcer = setInterval(() => {
                const APP = window.APP;
                const store = APP?.store;
                if (!store || !APP?.conference) {
                    return;
                }
                const coachId = APP.conference.getMyUserId?.();
                if (!coachId) {
                    console.log('[LiveCoaching][ENFORCER] En attente du myUserId...');
                    return;
                }
                const state = store.getState();
                const pinned = state['features/base/participants'].pinnedParticipant;
                const largeVideoId = state['features/large-video']?.participantId;
                const filmCfg = state['features/base/config'].filmstrip;
                const filmstripVisible = state['features/filmstrip']?.visible;
                if (filmCfg?.disableStageFilmstrip !== true) {
                    console.log('[LiveCoaching][ENFORCER] disableStageFilmstrip écrasé → ré-application');
                    store.dispatch(updateConfig({ filmstrip: { disableStageFilmstrip: true } }));
                }
                if (filmstripVisible) {
                    console.log('[LiveCoaching][ENFORCER] Filmstrip visible → fermeture');
                    store.dispatch(setFilmstripVisible(false));
                }
                if (pinned !== coachId || largeVideoId !== coachId) {
                    console.log('[LiveCoaching][ENFORCER] Forçage focus coach', {
                        coachId,
                        currentPinned: pinned,
                        currentLargeVideo: largeVideoId
                    });
                    store.dispatch(pinParticipant(coachId));
                    store.dispatch(selectParticipantInLargeVideo(coachId));
                }
            }, 1000);
            window.__lcCoachEnforcer = enforcer;
        }
        if (mode === 'viewer') {
            console.log('[LiveCoaching] Viewer → injection CSS + UI simplifiée');
            // Intercept getUserMedia so the browser never shows the camera/mic permission dialog.
            // Jitsi already handles NotAllowedError gracefully (logs a warning and continues
            // without local tracks), so rejecting immediately has no visible side-effect.
            if (navigator.mediaDevices?.getUserMedia) {
                const originalGUM = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
                navigator.mediaDevices.getUserMedia = (constraints) => {
                    if (constraints?.audio || constraints?.video) {
                        return Promise.reject(new DOMException('Viewer mode: no local media needed', 'NotAllowedError'));
                    }
                    return originalGUM(constraints);
                };
            }
            // Patch HTMLMediaElement.play to silently swallow autoplay errors.
            // Jitsi's internal track playback calls play() before the user has clicked
            // the overlay, which throws NotAllowedError. Without this patch, the error
            // surfaces as an uncaught runtime error. Our overlay click will retry play()
            // later, which will succeed once the user has interacted.
            const originalPlay = HTMLMediaElement.prototype.play;
            HTMLMediaElement.prototype.play = function () {
                const result = originalPlay.apply(this, arguments);
                if (result && typeof result.catch === 'function') {
                    return result.catch((err) => {
                        if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
                            // Autoplay blocked — silently ignored.
                            return undefined;
                        }
                        throw err;
                    });
                }
                return result;
            };
            const style = document.createElement('style');
            style.id = 'live-coaching-viewer-style';
            style.textContent = `
                #new-toolbox { display: none !important; }
                #participants-pane { display: none !important; }
                .details-container { display: none !important; }
                #remoteVideos { display: none !important; }
                div.filmstrip { display: none !important; }
                #notifications-container { display: none !important; }
                .display-avatar-only { display: none !important; }
                #dominantSpeaker { display: none !important; }
                #filmstripLocalVideo { display: none !important; }
                #filmstripLocalVideoThumbnail { display: none !important; }
            `;
            document.head.appendChild(style);
            // Waiting overlay shown until coach video stream appears.
            // Also serves as the mandatory user-interaction gate for browser autoplay policy.
            const overlay = document.createElement('div');
            overlay.id = 'live-coaching-waiting';
            overlay.innerHTML = `
                <div class="lc-waiting-content">
                    <div class="lc-pulse-ring"></div>
                    <div class="lc-pulse-ring lc-pulse-ring--delay"></div>
                    <div class="lc-icon">🎥</div>
                    <p class="lc-title">Votre coach arrive bientôt…</p>
                    <p class="lc-subtitle">La session démarrera dès que le coach sera connecté</p>
                    <div class="lc-dots">
                        <span></span><span></span><span></span>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);
            // The whole overlay acts as the interaction gate for browser autoplay policy.
            overlay.addEventListener('click', () => {
                if (!window.__lcAutoplayUnlocked) {
                    window.__lcAutoplayUnlocked = true;
                    document.querySelectorAll('video').forEach(v => {
                        v.play().catch(() => { });
                    });
                    console.log('[LiveCoaching] Autoplay débloqué via clic sur overlay');
                }
            }, { once: false });
            const waitingStyle = document.createElement('style');
            waitingStyle.textContent = `
                #live-coaching-waiting {
                    position: fixed;
                    inset: 0;
                    z-index: 9999;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #1a1a2e;
                    transition: opacity 0.6s ease;
                }
                #live-coaching-waiting.hidden {
                    opacity: 0;
                    pointer-events: none;
                }
                .lc-waiting-content {
                    text-align: center;
                    color: white;
                    position: relative;
                }
                .lc-pulse-ring {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    border: 3px solid rgba(99, 179, 237, 0.4);
                    animation: lc-pulse 2s ease-out infinite;
                }
                .lc-pulse-ring--delay {
                    animation-delay: 1s;
                }
                @keyframes lc-pulse {
                    0%   { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
                    100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
                }
                .lc-icon {
                    font-size: 56px;
                    margin-bottom: 24px;
                    animation: lc-float 3s ease-in-out infinite;
                }
                @keyframes lc-float {
                    0%, 100% { transform: translateY(0); }
                    50%       { transform: translateY(-10px); }
                }
                .lc-title {
                    font-size: 24px;
                    font-weight: 600;
                    margin: 0 0 8px;
                    letter-spacing: 0.3px;
                }
                .lc-subtitle {
                    font-size: 14px;
                    opacity: 0.6;
                    margin: 0 0 32px;
                }
                .lc-dots {
                    display: flex;
                    justify-content: center;
                    gap: 8px;
                }
                .lc-dots span {
                    width: 8px;
                    height: 8px;
                    background: #63b3ed;
                    border-radius: 50%;
                    animation: lc-bounce 1.4s ease-in-out infinite;
                }
                .lc-dots span:nth-child(2) { animation-delay: 0.2s; }
                .lc-dots span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes lc-bounce {
                    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
                    40%           { transform: scale(1);   opacity: 1; }
                }
                #live-coaching-waiting {
                    cursor: default;
                }
            `;
            document.head.appendChild(waitingStyle);
            console.log('[LiveCoaching] Overlay d\'attente injecté');
            dispatch(updateConfig({
                startSilent: true,
                startWithVideoMuted: true,
                disableTileView: true,
                p2p: { enabled: false },
                participantsPane: { enabled: false },
                conferenceInfo: {
                    alwaysVisible: [],
                    autoHide: []
                }
            }));
            // Verify config was applied
            setTimeout(() => {
                const s = window.APP?.store?.getState?.();
                if (s) {
                    const cfg = s['features/base/config'];
                    console.log('[LiveCoaching] Config après updateConfig:', {
                        liveCoachingMode: cfg.liveCoachingMode,
                        participantsPane: cfg.participantsPane,
                        conferenceInfo: cfg.conferenceInfo,
                        prejoinConfigEnabled: cfg.prejoinConfig?.enabled
                    });
                }
            }, 500);
        }
    }
    const result = next(action);
    // Coach mode: re-enforce focus on the coach immediately when something
    // could shift the large video (new participant, departure, dominant speaker
    // change, new remote track). The 1s polling enforcer would otherwise leave
    // a visible window during which a joiner's avatar takes over the stage.
    if (action.type === PARTICIPANT_JOINED
        || action.type === PARTICIPANT_LEFT
        || action.type === DOMINANT_SPEAKER_CHANGED
        || action.type === TRACK_ADDED) {
        const urlMode = new URLSearchParams(window.location.search).get('config.liveCoachingMode');
        const reduxMode = getState()['features/base/config'].liveCoachingMode;
        if (urlMode === 'coach' || reduxMode === 'coach') {
            const coachId = window.APP?.conference?.getMyUserId?.();
            if (coachId) {
                queueMicrotask(() => {
                    const s = getState();
                    const pinned = s['features/base/participants'].pinnedParticipant;
                    const largeId = s['features/large-video']?.participantId;
                    if (pinned !== coachId) {
                        dispatch(pinParticipant(coachId));
                    }
                    if (largeId !== coachId) {
                        dispatch(selectParticipantInLargeVideo(coachId));
                    }
                });
            }
        }
    }
    if (action.type === CONFERENCE_JOINED) {
        const state = getState();
        const urlMode = new URLSearchParams(window.location.search).get('config.liveCoachingMode');
        const liveCoachingMode = state['features/base/config'].liveCoachingMode ?? urlMode;
        const isModerator = isLocalParticipantModerator(state);
        console.log('[LiveCoaching] CONFERENCE_JOINED', {
            liveCoachingMode,
            isModerator,
            localParticipant: state['features/base/participants'].local
        });
        if (liveCoachingMode === 'coach') {
            if (isModerator) {
                console.log('[LiveCoaching] Coach modérateur → désactivation lobby + activation AV moderation + qualité max');
                dispatch(toggleLobbyMode(false));
                dispatch(requestEnableAudioModeration());
                dispatch(requestEnableVideoModeration());
                dispatch(requestEnableDesktopModeration());
                dispatch(setPreferredVideoQuality(MAX_VIDEO_QUALITY));
                dispatch(setFilmstripVisible(false));
                // Focus on own camera is handled by the polling enforcer started at APP_WILL_MOUNT.
            }
            else {
                console.warn('[LiveCoaching] Coach URL utilisée mais participant NON modérateur → AV moderation NON activée');
            }
        }
        else if (liveCoachingMode === 'viewer') {
            console.log('[LiveCoaching] Viewer rejoint la salle — qualité max + diagnostic DOM dans 2s...');
            dispatch(setPreferredVideoQuality(MAX_VIDEO_QUALITY));
            setTimeout(() => {
                // Large video
                const largeVideo = document.getElementById('largeVideo');
                const largeVideoContainer = document.getElementById('largeVideoContainer');
                const largeVideoWrapper = document.getElementById('largeVideoWrapper');
                console.log('[LiveCoaching] #largeVideoContainer:', {
                    exists: Boolean(largeVideoContainer),
                    display: largeVideoContainer ? getComputedStyle(largeVideoContainer).display : 'N/A',
                    visibility: largeVideoContainer ? getComputedStyle(largeVideoContainer).visibility : 'N/A',
                    classes: largeVideoContainer?.className
                });
                console.log('[LiveCoaching] #largeVideo (video element):', {
                    exists: Boolean(largeVideo),
                    display: largeVideo ? getComputedStyle(largeVideo).display : 'N/A',
                    src: largeVideo?.src || largeVideo?.srcObject ? 'has source' : 'NO SOURCE',
                    paused: largeVideo?.paused,
                    readyState: largeVideo?.readyState
                });
                console.log('[LiveCoaching] #largeVideoWrapper:', {
                    exists: Boolean(largeVideoWrapper),
                    display: largeVideoWrapper ? getComputedStyle(largeVideoWrapper).display : 'N/A',
                    classes: largeVideoWrapper?.className
                });
                // All video elements in the page
                const allVideos = document.querySelectorAll('video');
                console.log(`[LiveCoaching] Tous les éléments <video> (${allVideos.length}):`, Array.from(allVideos).map(v => ({
                    id: v.id,
                    parent: v.parentElement?.id || v.parentElement?.className,
                    display: getComputedStyle(v).display,
                    hasSrc: Boolean(v.src || v.srcObject),
                    paused: v.paused,
                    readyState: v.readyState
                })));
                // Check which CSS rules affect largeVideoContainer
                const allVideoContainers = document.querySelectorAll('.videocontainer');
                console.log(`[LiveCoaching] Éléments .videocontainer (${allVideoContainers.length}):`, Array.from(allVideoContainers).map(el => ({
                    id: el.id,
                    classes: el.className,
                    display: getComputedStyle(el).display
                })));
            }, 2000);
        }
        else {
            console.log('[LiveCoaching] Mode coaching inactif, comportement normal');
        }
    }
    // Viewer: show/hide waiting overlay based on remote video tracks
    // (Coach focus is handled by the polling enforcer started at APP_WILL_MOUNT)
    if (action.type === TRACK_ADDED || action.type === TRACK_REMOVED || action.type === TRACK_UPDATED) {
        // Read from URL directly — more reliable than Redux state during early lifecycle
        const urlMode = new URLSearchParams(window.location.search).get('config.liveCoachingMode');
        const state = getState();
        const reduxMode = state['features/base/config'].liveCoachingMode;
        if (urlMode === 'viewer' || reduxMode === 'viewer') {
            const tracks = state['features/base/tracks'];
            const remoteVideoTracks = tracks.filter((t) => !t.local && t.mediaType === 'video');
            console.log(`[LiveCoaching] remote video tracks (${remoteVideoTracks.length}):`, remoteVideoTracks.map((t) => ({
                participantId: t.participantId,
                muted: t.muted,
                videoType: t.videoType
            })));
            const hasRemoteVideo = remoteVideoTracks.some((t) => !t.muted);
            // When the first remote video track arrives, pin it in the large video
            // so the coach appears full-screen regardless of Jitsi's dominant-speaker detection.
            if (action.type === TRACK_ADDED && !action.track?.local
                && action.track?.mediaType === 'video' && !action.track?.muted) {
                dispatch(selectParticipantInLargeVideo(action.track.participantId));
            }
            const overlay = document.getElementById('live-coaching-waiting');
            console.log('[LiveCoaching] hasRemoteVideo:', hasRemoteVideo, '| autoplayUnlocked:', window.__lcAutoplayUnlocked);
            if (overlay) {
                if (hasRemoteVideo) {
                    // Cancel any pending "show overlay" timer — track came back (P2P switch, etc.)
                    if (window.__lcOverlayTimer) {
                        clearTimeout(window.__lcOverlayTimer);
                        window.__lcOverlayTimer = undefined;
                    }
                    if (window.__lcAutoplayUnlocked) {
                        // User already clicked → safe to hide and play
                        console.log('[LiveCoaching] ✅ Flux vidéo + autoplay débloqué → masquage overlay');
                        overlay.classList.add('hidden');
                        setTimeout(() => {
                            overlay.style.display = 'none';
                        }, 650);
                        document.querySelectorAll('video').forEach(v => {
                            v.play().catch(() => {
                                // ignore autoplay errors
                            });
                        });
                    }
                    else {
                        // Coach already present but user hasn't clicked yet → prompt them
                        console.log('[LiveCoaching] ✅ Flux vidéo coach → attente clic utilisateur (autoplay policy)');
                        const title = overlay.querySelector('.lc-title');
                        const subtitle = overlay.querySelector('.lc-subtitle');
                        const icon = overlay.querySelector('.lc-icon');
                        if (title) {
                            title.textContent = 'Le coach est en ligne !';
                        }
                        if (subtitle) {
                            subtitle.textContent = 'Cliquez pour rejoindre la session';
                        }
                        if (icon) {
                            icon.textContent = '▶️';
                        }
                        overlay.style.cursor = 'pointer';
                        // Override the click handler to also hide overlay
                        overlay.onclick = () => {
                            if (!window.__lcAutoplayUnlocked) {
                                window.__lcAutoplayUnlocked = true;
                                document.querySelectorAll('video').forEach(v => {
                                    v.play().catch(() => {
                                        // ignore autoplay errors
                                    });
                                });
                                console.log('[LiveCoaching] Autoplay débloqué via clic (coach déjà présent)');
                            }
                            overlay.classList.add('hidden');
                            setTimeout(() => {
                                overlay.style.display = 'none';
                            }, 650);
                        };
                    }
                }
                else {
                    // Debounce: P2P↔JVB switches briefly remove the track before re-adding it.
                    // Only show the overlay after 3s without video to avoid flickering.
                    const existing = window.__lcOverlayTimer;
                    if (!existing) {
                        console.log('[LiveCoaching] ⏳ Pas de flux vidéo → overlay dans 3s si toujours absent');
                        window.__lcOverlayTimer = setTimeout(() => {
                            window.__lcOverlayTimer = undefined;
                            const o = document.getElementById('live-coaching-waiting');
                            if (o) {
                                o.style.display = 'flex';
                                setTimeout(() => o.classList.remove('hidden'), 10);
                            }
                        }, 3000);
                    }
                }
            }
        }
    }
    if (action.type === CONFERENCE_FAILED) {
        const state = getState();
        const { liveCoachingMode } = state['features/base/config'];
        console.log('[LiveCoaching] CONFERENCE_FAILED', {
            liveCoachingMode,
            error: action.error?.name
        });
        if (liveCoachingMode === 'viewer'
            && action.error?.name === JitsiConferenceErrors.MEMBERS_ONLY_ERROR) {
            // The room has lobby enabled from a previous session.
            // Log clearly so the coach knows they must join first to disable it.
            console.warn('[LiveCoaching] Viewer bloqué par le lobby. Le coach doit rejoindre en premier pour désactiver le lobby.');
        }
    }
    return result;
});
